import { useState } from "react";
import "./App.css";
import Sidebar from "./components/Sidebar";
import Header from "./components/Header";
import DashboardHome from "./components/DashboardHome";
import SessionStatus from "./components/session/SessionStatus";
import { Routes, Route, Navigate } from "react-router-dom";
import ChatPage from "./components/chat/ChatPage";
import AutoReplyPage from "./components/autoreply/AutoReplyPage";
import ProfilePage from "./components/ProfilePage";
import SendMessagePage from "./components/chat/SendMessagePage";
import LoginPage from "./components/auth/LoginPage";
import RegisterPage from "./components/auth/RegisterPage";
import ForgotPasswordPage from "./components/auth/ForgotPasswordPage";
import ClientPage from "./components/ClientPage";
import GroupPage from "./components/GroupPage";
import LandingPage from "./components/landing/LandingPage";
import NotFoundPage from "./components/NotFoundPage";
import { getLang } from "./i18n";
import UserManagementPage from "./components/superadmin/UserManagementPage";
import ApiDocsCenter from "./components/ApiDocsCenter";
import InboxPage from "./components/saas/InboxPage";
import GrowthPage from "./components/saas/GrowthPage";
import AutomationStudio from "./components/saas/AutomationStudio";
import WorkspaceSuite from "./components/saas/WorkspaceSuite";

function RequireAuth({ children }) {
  const token = localStorage.getItem("access_token");
  if (!token) {
    return <Navigate to="/auth/login" replace />;
  }
  return children;
}

function RequireSuperadmin({ children }) {
  if (localStorage.getItem("role") !== "superadmin") {
    return <Navigate to="/admin" replace />;
  }
  return children;
}

function App() {
  const [sidebarOpen, setSidebarOpen] = useState(() =>
    window.matchMedia("(min-width: 1024px)").matches
  );

  const handleSidebarToggle = () => setSidebarOpen((open) => !open);

  const lang = getLang();
  const isRTL = lang === "ar";
  const sidebarMargin = isRTL
    ? sidebarOpen
      ? "lg:mr-72"
      : "lg:mr-20"
    : sidebarOpen
    ? "lg:ml-72"
    : "lg:ml-20";
  return (
    <div
      className="min-h-screen bg-slate-50 text-slate-900"
      dir={isRTL ? "rtl" : "ltr"}
      lang={lang}
    >
      <Routes>
        <Route
          path="/auth/login"
          element={
            <main className="flex-1 p-0 bg-gray-100">
              <LoginPage />
            </main>
          }
        />

        <Route
          path="/auth/register"
          element={
            <main className="flex-1 p-0 bg-gray-100">
              <RegisterPage />
            </main>
          }
        />
        <Route
          path="/auth/forgot-password"
          element={
            <main className="min-h-screen bg-slate-50">
              <ForgotPasswordPage />
            </main>
          }
        />
        <Route path="/" element={<LandingPage />} />

        {/* Change: make admin area only match /admin/* so global 404 can render without header/sidebar */}
        <Route
          path="/admin/*"
          element={
            <RequireAuth>
              <>
                {sidebarOpen && (
                  <button
                    type="button"
                    className="fixed inset-0 z-30 bg-slate-950/35 backdrop-blur-[1px] lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                    aria-label={isRTL ? "إغلاق القائمة" : "Close navigation"}
                  />
                )}
                <Sidebar
                  open={sidebarOpen}
                  onToggle={handleSidebarToggle}
                  onNavigate={() => {
                    if (window.innerWidth < 1024) setSidebarOpen(false);
                  }}
                />
                <div
                  className={`flex min-h-screen flex-col transition-[margin] duration-300 ${sidebarMargin}`}
                >
                  <Header
                    onSidebarToggle={handleSidebarToggle}
                    sidebarOpen={sidebarOpen}
                  />
                  <main id="main-content" className="flex-1 px-4 py-5 sm:px-6 lg:px-8 lg:py-7">
                    <Routes>
                      <Route index element={<DashboardHome />} />
                      <Route path="dashboard" element={<Navigate to="/admin" replace />} />
                      <Route path="session" element={<SessionStatus />} />
                      <Route
                        path="chat"
                        element={
                          <ChatPage
                            onSidebarToggle={handleSidebarToggle}
                            sidebarOpen={sidebarOpen}
                          />
                        }
                      />
                      <Route path="auto-reply" element={<AutoReplyPage />} />
                      <Route path="profile" element={<ProfilePage />} />
                      <Route path="send-message" element={<SendMessagePage />} />
                      <Route path="client" element={<ClientPage />} />
                      <Route path="groups" element={<GroupPage />} />
                      <Route path="api-docs" element={<ApiDocsCenter />} />
                      <Route path="inbox" element={<InboxPage />} />
                      <Route path="growth" element={<GrowthPage />} />
                      <Route path="automation-studio" element={<AutomationStudio />} />
                      <Route path="workspace" element={<WorkspaceSuite />} />
                      <Route
                        path="users"
                        element={
                          <RequireSuperadmin>
                            <UserManagementPage />
                          </RequireSuperadmin>
                        }
                      />
                      <Route path="*" element={<NotFoundPage compact />} />
                    </Routes>
                  </main>
                </div>
              </>
            </RequireAuth>
          }
        />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </div>
  );
}

export default App;
